import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { FaPlus, FaEdit, FaEye, FaFilter, FaChevronDown, FaChevronUp, FaCalendarAlt } from 'react-icons/fa';
import NoRecordFound from '../../../components/common/no-record-found';
import ViewModal from '../../../components/common/ViewModal';

const ROWS_PER_PAGE = 25;

const PurchaseContractList = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalQtyMt, setTotalQtyMt] = useState(0);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // ✅ Added state for date filters and filter visibility
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [filters, setFilters] = useState({
    contractDateFrom: '',
    contractDateTo: '',
    orderType: '',
    currency: '',
    commodityId: ''
  });
  const [commodities, setCommodities] = useState([]);

  const formatOrderType = (orderType) => {
    if (!orderType) return '-';
    if (orderType === 'HS') return 'High Sea';
    if (orderType === 'DIRECT') return 'Origin';
    return orderType;
  };
// Add this function after formatNumber
const formatShipmentPeriod = (startDate, endDate) => {
  if (!startDate || !endDate) return '-';
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  return `${start} - ${end}`;
};

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  // ✅ Filter change handler
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(1); // Reset to first page when filters change
  };

  // ✅ Reset filters function
  const resetFilters = () => {
    setFilters({
      contractDateFrom: '',
      contractDateTo: '',
      orderType: '',
      currency: '',
      commodityId: ''
    });
    setPage(1);
  };

  // ✅ Toggle date filters visibility
  const toggleDateFilters = () => {
    setShowDateFilters(!showDateFilters);
  };

  // ✅ Clear all filters function
  const clearAllFilters = () => {
    setSearch('');
    setFilters({
      contractDateFrom: '',
      contractDateTo: '',
      orderType: '',
      currency: '',
      commodityId: ''
    });
    setShowDateFilters(false);
    setPage(1);
  };

  const fetchRecords = async (page, searchValue) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: searchValue || '',
      });

      // ✅ Add date filters to params
      if (filters.contractDateFrom) {
        params.append('contractDateFrom', filters.contractDateFrom);
      }
      if (filters.contractDateTo) {
        params.append('contractDateTo', filters.contractDateTo);
      }
      if (filters.orderType) {
        params.append('orderType', filters.orderType);
      }
      if (filters.currency) {
        params.append('currency', filters.currency);
      }
      if (filters.commodityId) {
        params.append('commodityId', filters.commodityId);
      }

      const res = await fetch(`${API_BASE_URL}/api/contracts/purchase-contract-get?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const result = await res.json();
        
      // Debug: Log first record to see warehouse and CHA data
      if (result.data && result.data.length > 0) {
        console.log('Frontend received data:', {
          warehouse_id: result.data[0].warehouse_id,
          warehouse_name: result.data[0].warehouse_name,
          warehouse_type: result.data[0].warehouse_type,
          warehouse_location: result.data[0].warehouse_location,
          warehouse_state: result.data[0].warehouse_state,
          cha_cma_id: result.data[0].cha_cma_id,
          cma_name: result.data[0].cma_name,
          supplier_name: result.data[0].supplier_name,
          broker_name: result.data[0].broker_name
        });
      }
       
      setRecords(result.data || []);
      setTotal(result.total || 0);
      setTotalQtyMt(result.total_qty_mt || 0);
    } catch (err) {
      setError('Failed to load data');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch commodities for filter
  useEffect(() => {
    const fetchCommodities = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-commodities`, {
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          setCommodities(data.data || []);
        }
      } catch (error) {
        console.error('Error fetching commodities:', error);
      }
    };
    fetchCommodities();
  }, []);

  // ✅ Updated useEffect to include filters dependency
  useEffect(() => {
    fetchRecords(page, debouncedSearch);
    // eslint-disable-next-line
  }, [page, debouncedSearch, filters]);

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatNumber = (number, decimals = 2) => {
    if (number === null || number === undefined || number === '') return '-';
    return parseFloat(number).toFixed(decimals);
  };

  const handleViewRecord = (record) => {
    console.log('Modal record data:', {
      cha_cma_id: record.cha_cma_id,
      cma_name: record.cma_name,
      supplier_name: record.supplier_name,
      broker_name: record.broker_name,
      warehouse_name: record.warehouse_name
    });
    setSelectedRecord(record);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRecord(null);
  };

  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-xl font-semibold">Purchase Contracts</h2>
        <div className="flex gap-1 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={handleSearchChange}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-48 text-sm"
          />
          {/* ✅ Date Filter Toggle Button */}
          <button
            onClick={toggleDateFilters}
            className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 border border-blue-300 rounded hover:bg-blue-100 text-xs font-medium transition-colors"
          >
            <FaFilter className="text-blue-600" />
            {showDateFilters ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
            {(filters.contractDateFrom || filters.contractDateTo || filters.orderType || filters.currency || filters.commodityId) && (
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
            )}
          </button>
          <button
            className="flex items-center gap-1 bg-[#10a5e9] text-white px-3 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#10a5e9] font-medium"
            onClick={() => navigate('/purchase-contract/new')}
          >
            <FaPlus />
            Add New
          </button>
        </div>
      </div>

      {/* ✅ Collapsible Date Filter Section */}
      {showDateFilters && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <FaCalendarAlt className="text-blue-600" />
              Contract Date Filter
            </h4>
            {(search || filters.contractDateFrom || filters.contractDateTo || filters.typeOfPurchase || filters.currency || filters.commodityId) && (
              <button
                onClick={clearAllFilters}
                className="px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Clear All Filters
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-600 mb-1">Contract Date From</label>
              <input
                type="date"
                name="contractDateFrom"
                value={filters.contractDateFrom}
                onChange={handleFilterChange}
                className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-600 mb-1">Contract Date To</label>
              <input
                type="date"
                name="contractDateTo"
                value={filters.contractDateTo}
                onChange={handleFilterChange}
                className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-600 mb-1">Type of Purchase</label>
              <select
                name="orderType"
                value={filters.orderType}
                onChange={handleFilterChange}
                className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All</option>
                <option value="Origin">Origin</option>
                <option value="High Sea">High Sea</option>
                <option value="Domestic">Domestic</option>
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-600 mb-1">Currency</label>
              <select
                name="currency"
                value={filters.currency}
                onChange={handleFilterChange}
                className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All</option>
                <option value="USD">USD</option>
                <option value="INR">INR</option>
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-600 mb-1">Commodity</label>
              <select
                name="commodityId"
                value={filters.commodityId}
                onChange={handleFilterChange}
                className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All</option>
                {commodities.map(commodity => (
                  <option key={commodity.commodity_id} value={commodity.commodity_id}>
                    {commodity.name} {commodity.origin ? `(${commodity.origin})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

            {(filters.contractDateFrom || filters.contractDateTo || filters.orderType || filters.currency || filters.commodityId) && (
              <div className="mt-3 flex items-center justify-between bg-white p-2 rounded border border-blue-200">
                <span className="text-xs text-blue-700 font-medium">
                  Active filters: {[
                    filters.contractDateFrom && filters.contractDateTo ? `Date: ${filters.contractDateFrom} to ${filters.contractDateTo}` : 
                    filters.contractDateTo ? `Date: up to ${filters.contractDateTo}` : 
                    filters.contractDateFrom ? `Date: from ${filters.contractDateFrom}` : null,
                    filters.orderType ? `Order Type: ${formatOrderType(filters.orderType)}` : null,
                    filters.currency ? `Currency: ${filters.currency}` : null,
                    filters.commodityId ? `Commodity: ${commodities.find(c => c.commodity_id === parseInt(filters.commodityId))?.name || filters.commodityId}` : null
                  ].filter(Boolean).join(', ')}
                </span>
                <button
                  onClick={resetFilters}
                  className="px-2 py-1 text-xs bg-blue-200 text-blue-700 rounded hover:bg-blue-300 font-medium"
                >
                  Reset Filters
                </button>
              </div>
            )}
        </div>
      )}

      {loading ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-sm">{error}</div>
      ) : records.length === 0 ? (
        <NoRecordFound message="No purchase contract records found." />
      ) : (
        <div className="overflow-x-auto rounded border border-gray-50">
          <table className="min-w-full text-sm whitespace-nowrap">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Contract No</th>
                <th className="px-3 py-2 text-left font-medium">Date</th>
                <th className="px-3 py-2 text-left font-medium">State</th>
                <th className="px-3 py-2 text-left font-medium">Commodity</th>
                <th className="px-3 py-2 text-left font-medium">Commodity With Full Name</th>
                <th className="px-3 py-2 text-left font-medium">Expected Shipment Period</th>
                <th className="px-3 py-2 text-left font-medium">Qty (MT)</th>
                <th className="px-3 py-2 text-left font-medium">Type of Purchase</th>
                <th className="px-3 py-2 text-left font-medium">Currency</th>
                <th className="px-3 py-2 text-left font-medium">Unit Price</th>
                <th className="px-3 py-2 text-left font-medium">Incoterm</th>
                <th className="px-3 py-2 text-left font-medium">Actions</th>
              </tr>
            </thead>
           <tbody>
    {records.map((item, idx) => (
      <tr key={item.purchase_contract_no} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
        <td className="px-3 py-1 font-semibold">{item.purchase_contract_no}</td>
        <td className="px-3 py-1">{formatDate(item.contract_date)}</td>
        <td className="px-3 py-1">{item.state_of_purchase || '-'}</td>
        
        {/* Updated Commodity column */}
        <td className="px-3 py-1">
          <div 
            className="truncate max-w-[160px] cursor-pointer" 
            title={item.commodity_name || '-'}
          >
            {item.commodity_name || '-'}
          </div>
        </td>
        
        {/* Updated Commodity Full Name column */}
        <td className="px-3 py-1">
          <div 
            className="truncate max-w-[200px] cursor-pointer" 
            title={item.commodity_full || '-'}
          >
            {item.commodity_full || '-'}
          </div>
        </td>
        <td className="px-3 py-1 text-sm">
        {formatShipmentPeriod(item.expected_shipment_start, item.expected_shipment_end)}
      </td>
        <td className="px-3 py-1">{formatNumber(item.total_qty_mt, 3)}</td>
        <td className="px-3 py-1">{formatOrderType(item.order_type)}</td>
        <td className="px-3 py-1">{item.currency || '-'}</td>
        <td className="px-3 py-1">
          {item.currency === 'USD' ? '$' : '₹'}{formatNumber(item.unit_price_usd, 2)}
        </td>
        <td className="px-3 py-1">{item.incoterm || '-'}</td>
        <td className="px-3 py-1">
          <div className="flex gap-1">
            <button
              onClick={() => handleViewRecord(item)}
              className="flex items-center gap-1 bg-green-600 text-white px-2 py-1 text-xs rounded hover:bg-green-700 border border-green-600 font-medium"
            >
              <FaEye />
              View
            </button>
            <button
              onClick={() => {
                console.log("Edit contract ID:", item.id);
                navigate(`/purchase-contract/edit/${item.id}`);
              }}
              className="flex items-center gap-1 bg-[#10a5e9] text-white px-2 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#10a5e9] font-medium"
            >
              <FaEdit />
              Edit
            </button>
          </div>
        </td>
      </tr>
    ))}
  </tbody>
            {/* Totals Row - Only show on last page with total from all pages */}
            {page === totalPages && (
              <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                <tr className="font-bold text-gray-900">
                  <td className="px-3 py-2 text-left" colSpan="6">
                    <span className="text-sm">TOTAL:</span>
                  </td>
                  <td className="px-3 py-2 text-left font-bold text-blue-900">
                    {parseFloat(totalQtyMt).toFixed(3)} MT
                  </td>
                  <td className="px-3 py-2 text-left" colSpan="5"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {total > ROWS_PER_PAGE && (
        <div className="flex justify-end items-center gap-2 mt-3 text-xs">
          <button
            onClick={handlePrevPage}
            disabled={page === 1}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === 1 ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Prev
          </button>
          <span className="text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={page === totalPages}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === totalPages ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Next
          </button>
        </div>
      )}

      {/* View Modal - Keep existing modal code unchanged */}
      <ViewModal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={`Purchase Contract Details - ${selectedRecord?.purchase_contract_no || ''}`}
        size="xl"
      >
        {selectedRecord && (
          <div className="space-y-6">
            {/* Header Section with Contract Number */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">
                    {selectedRecord.purchase_contract_no}
                  </h2>
                  <p className="text-gray-600 font-medium">
                    Contract Date: {formatDate(selectedRecord.contract_date)}
                  </p>
                </div>
              </div>
            </div>

            {/* Complete Contract Details */}
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 rounded-t-lg">
                  <h3 className="text-lg font-semibold text-gray-800">Basic Information</h3>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-gray-600">Contract Number</span>
                      <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
                        {selectedRecord.purchase_contract_no}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-gray-600">Contract Date</span>
                      <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
                        {formatDate(selectedRecord.contract_date)}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-gray-600">State of Purchase</span>
                      <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
                        {selectedRecord.state_of_purchase || '-'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-gray-600">State Code</span>
                      <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
                        {selectedRecord.state_code || '-'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-gray-600">Incoterm</span>
                      <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
                        {selectedRecord.incoterm || '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Commodity Information */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 rounded-t-lg">
                  <h3 className="text-lg font-semibold text-gray-800">Commodity Information</h3>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-gray-600">Commodity Name</span>
                      <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
                        {selectedRecord.commodity_name || '-'}
                      </p>
                    </div>
                    <div className="md:col-span-2 space-y-1">
                      <span className="text-sm font-medium text-gray-600">Commodity Full Name</span>
                      <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
                        {selectedRecord.commodity_full || '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Parties Information */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 rounded-t-lg">
                  <h3 className="text-lg font-semibold text-gray-800">Parties Information</h3>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-gray-600">Supplier</span>
                      <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
                        {selectedRecord.supplier_name || '-'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-gray-600">Broker</span>
                      <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
                        {selectedRecord.broker_name || '-'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-gray-600">CHA/CMA</span>
                      <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
                        {selectedRecord.cma_name || '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Warehouse Information */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 rounded-t-lg">
                  <h3 className="text-lg font-semibold text-gray-800">Warehouse Information</h3>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-gray-600">Warehouse Name</span>
                      <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
                        {selectedRecord.warehouse_name || '-'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-gray-600">Type</span>
                      <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
                        {selectedRecord.warehouse_type || '-'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-gray-600">Location</span>
                      <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
                        {selectedRecord.warehouse_location || '-'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-gray-600">State</span>
                      <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
                        {selectedRecord.warehouse_state || '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
{/* Shipment Information */}
<div className="bg-white border border-gray-200 rounded-lg shadow-sm">
  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 rounded-t-lg">
    <h3 className="text-lg font-semibold text-gray-800">Shipment Information</h3>
  </div>
  <div className="p-4">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="space-y-1">
        <span className="text-sm font-medium text-gray-600">Expected Shipment Start</span>
        <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
          {formatDate(selectedRecord.expected_shipment_start)}
        </p>
      </div>
      <div className="space-y-1">
        <span className="text-sm font-medium text-gray-600">Expected Shipment End</span>
        <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
          {formatDate(selectedRecord.expected_shipment_end)}
        </p>
      </div>
      <div className="space-y-1">
        <span className="text-sm font-medium text-gray-600">Shipment Period</span>
        <p className="text-sm font-semibold text-gray-900 bg-blue-50 p-2 rounded border border-blue-200">
          {formatShipmentPeriod(selectedRecord.expected_shipment_start, selectedRecord.expected_shipment_end)}
        </p>
      </div>
    </div>
  </div>
</div>

              {/* Quantity & Pricing Details */}
             {/* Quantity & Pricing Details */}
<div className="bg-white border border-gray-200 rounded-lg shadow-sm">
  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 rounded-t-lg">
    <h3 className="text-lg font-semibold text-gray-800">Quantity & Pricing Details</h3>
  </div>
  <div className="p-4">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <div className="space-y-1">
        <span className="text-sm font-medium text-gray-600">Type of Purchase</span>
        <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
          {formatOrderType(selectedRecord.order_type)}
        </p>
      </div>
      <div className="space-y-1">
        <span className="text-sm font-medium text-gray-600">Currency</span>
        <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
          {selectedRecord.currency || '-'}
        </p>
      </div>
      <div className="space-y-1">
        <span className="text-sm font-medium text-gray-600">Total Quantity (MT)</span>
        <p className="text-sm font-bold text-gray-900 bg-gray-50 p-2 rounded">
          {formatNumber(selectedRecord.total_qty_mt, 3)}
        </p>
      </div>
      <div className="space-y-1">
        <span className="text-sm font-medium text-gray-600">
          Unit Price PMT ({selectedRecord.currency === 'USD' ? '$' : '₹'})
        </span>
        <p className="text-sm font-bold text-gray-900 bg-gray-50 p-2 rounded">
         {formatNumber(selectedRecord.unit_price_usd, 2)}
        </p>
      </div>
      <div className="space-y-1">
        <span className="text-sm font-medium text-gray-600">Quantity Tolerance %</span>
        <p className="text-sm font-bold text-gray-900 bg-gray-50 p-2 rounded">
          {formatNumber(selectedRecord.quantity_tolerance_pct, 2)}%
        </p>
      </div>
      <div className="space-y-1">
        <span className="text-sm font-medium text-gray-600">Planned FX Rate</span>
        <p className="text-sm font-bold text-gray-900 bg-gray-50 p-2 rounded">
          {formatNumber(selectedRecord.planned_fx_rate, 4)}
        </p>
      </div>
      <div className="space-y-1">
        <span className="text-sm font-medium text-gray-600">Markup %</span>
        <p className="text-sm font-bold text-gray-900 bg-gray-50 p-2 rounded">
          {formatNumber(selectedRecord.markup_pct, 2)}%
        </p>
      </div>
      <div className="space-y-1">
        <span className="text-sm font-medium text-gray-600">Hedge Executed</span>
        <p className="text-sm font-bold text-gray-900 bg-gray-50 p-2 rounded">
          {selectedRecord.hedge_executed || '-'}
        </p>
      </div>
      <div className="space-y-1">
        <span className="text-sm font-medium text-gray-600">
          Total Value ({selectedRecord.currency === 'USD' ? '$' : '₹'})
        </span>
        <p className="text-sm font-bold text-gray-900 bg-blue-50 p-2 rounded border border-blue-200">
          
{formatNumber(selectedRecord.total_qty_mt * selectedRecord.unit_price_usd, 2)}
        </p>
      </div>
    </div>
  </div>
</div>

              {/* Remarks */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 rounded-t-lg">
                  <h3 className="text-lg font-semibold text-gray-800">Remarks</h3>
                </div>
                <div className="p-4">
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-800 leading-relaxed">
                      {selectedRecord.remarks || 'No remarks available for this contract.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Close
              </button>
              <button
                onClick={() => {
                  navigate(`/purchase-contract/edit/${selectedRecord.id}`);
                  closeModal();
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-600 border border-transparent rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Edit Contract
              </button>
            </div>
          </div>
        )}
      </ViewModal>
    </div>
  );
};

export default PurchaseContractList;
